import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CreateTimesheetApprovalPayload,
  CreateTimesheetEntryPayload,
  CreateTimesheetPayload,
  CursorPaginatedResponse,
  Timesheet,
  TimesheetApproval,
  TimesheetApprovalData,
  TimesheetApprovalListParams,
  TimesheetData,
  TimesheetEntry,
  TimesheetEntryData,
  TimesheetEntryListParams,
  TimesheetFull,
  TimesheetListParams,
  UpdateTimesheetApprovalPayload,
  UpdateTimesheetEntryPayload,
  UpdateTimesheetPayload,
} from '@/libs/interfaces'
import {
  timesheetApprovalsApiMock,
  timesheetEntriesApiMock,
  timesheetsApiMock,
} from '@/libs/mocks/handlers/timesheets'

const timesheetsApiReal = {
  create: async (
    orgID: string,
    payload: CreateTimesheetPayload,
  ): Promise<CreateApiResult<Timesheet>> => {
    return await api
      .post<ApiResponse<Timesheet>>(URLs.Timesheets.create(orgID), payload)
      .then((res) => ({
        data: res.data.data,
        status: res.status,
        message: res.message,
      }))
  },

  list: async (params: TimesheetListParams): Promise<Timesheet[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<TimesheetData>>(URLs.Timesheets.list(orgID, query))
      .then((res) => res.data.data.data)
  },

  listCursor: async (params: TimesheetListParams): Promise<CursorPaginatedResponse<Timesheet>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<TimesheetData>>(URLs.Timesheets.list(orgID, query))
      .then((res) => res.data.data)
    return {
      data: response.data,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  getByID: async (orgID: string, timesheetID: string): Promise<Timesheet> => {
    return await api
      .get<ApiResponse<Timesheet>>(URLs.Timesheets.byID(orgID, timesheetID))
      .then((res) => res.data.data)
  },

  getFull: async (orgID: string, timesheetID: string): Promise<TimesheetFull> => {
    return await api
      .get<ApiResponse<TimesheetFull>>(URLs.Timesheets.full(orgID, timesheetID))
      .then((res) => res.data.data)
  },

  update: async (
    orgID: string,
    timesheetID: string,
    payload: UpdateTimesheetPayload,
  ): Promise<Timesheet> => {
    return await api
      .patch<ApiResponse<Timesheet>>(URLs.Timesheets.byID(orgID, timesheetID), payload)
      .then((res) => res.data.data)
  },

  delete: async (orgID: string, timesheetID: string): Promise<Timesheet> => {
    return await api
      .delete<ApiResponse<Timesheet>>(URLs.Timesheets.byID(orgID, timesheetID))
      .then((res) => res.data.data)
  },
}

export const timesheetsApi: typeof timesheetsApiReal = __IS_MOCK_MODE__
  ? timesheetsApiMock
  : timesheetsApiReal

const timesheetEntriesApiReal = {
  create: async (
    orgID: string,
    payload: CreateTimesheetEntryPayload,
  ): Promise<CreateApiResult<TimesheetEntry>> => {
    return await api
      .post<ApiResponse<TimesheetEntry>>(URLs.TimesheetEntries.create(orgID), payload)
      .then((res) => ({
        data: res.data.data,
        status: res.status,
        message: res.message,
      }))
  },

  list: async (params: TimesheetEntryListParams): Promise<TimesheetEntry[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<TimesheetEntryData>>(URLs.TimesheetEntries.list(orgID, query))
      .then((res) => res.data.data.data)
  },

  listCursor: async (
    params: TimesheetEntryListParams,
  ): Promise<CursorPaginatedResponse<TimesheetEntry>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<TimesheetEntryData>>(URLs.TimesheetEntries.list(orgID, query))
      .then((res) => res.data.data)
    return {
      data: response.data,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  getByID: async (orgID: string, timesheetEntryID: string): Promise<TimesheetEntry> => {
    return await api
      .get<ApiResponse<TimesheetEntry>>(URLs.TimesheetEntries.byID(orgID, timesheetEntryID))
      .then((res) => res.data.data)
  },

  update: async (
    orgID: string,
    timesheetEntryID: string,
    payload: UpdateTimesheetEntryPayload,
  ): Promise<TimesheetEntry> => {
    return await api
      .patch<ApiResponse<TimesheetEntry>>(
        URLs.TimesheetEntries.byID(orgID, timesheetEntryID),
        payload,
      )
      .then((res) => res.data.data)
  },

  delete: async (orgID: string, timesheetEntryID: string): Promise<TimesheetEntry> => {
    return await api
      .delete<ApiResponse<TimesheetEntry>>(URLs.TimesheetEntries.byID(orgID, timesheetEntryID))
      .then((res) => res.data.data)
  },
}

export const timesheetEntriesApi: typeof timesheetEntriesApiReal = __IS_MOCK_MODE__
  ? timesheetEntriesApiMock
  : timesheetEntriesApiReal

const timesheetApprovalsApiReal = {
  create: async (
    orgID: string,
    payload: CreateTimesheetApprovalPayload,
  ): Promise<CreateApiResult<TimesheetApproval>> => {
    return await api
      .post<ApiResponse<TimesheetApproval>>(URLs.TimesheetApprovals.create(orgID), payload)
      .then((res) => ({
        data: res.data.data,
        status: res.status,
        message: res.message,
      }))
  },

  list: async (params: TimesheetApprovalListParams): Promise<TimesheetApproval[]> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<TimesheetApprovalData>>(URLs.TimesheetApprovals.list(orgID, query))
      .then((res) => res.data.data.data)
  },

  listCursor: async (
    params: TimesheetApprovalListParams,
  ): Promise<CursorPaginatedResponse<TimesheetApproval>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<TimesheetApprovalData>>(URLs.TimesheetApprovals.list(orgID, query))
      .then((res) => res.data.data)
    return {
      data: response.data,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  getByID: async (orgID: string, timesheetApprovalID: string): Promise<TimesheetApproval> => {
    return await api
      .get<ApiResponse<TimesheetApproval>>(URLs.TimesheetApprovals.byID(orgID, timesheetApprovalID))
      .then((res) => res.data.data)
  },

  update: async (
    orgID: string,
    timesheetApprovalID: string,
    payload: UpdateTimesheetApprovalPayload,
  ): Promise<TimesheetApproval> => {
    return await api
      .patch<ApiResponse<TimesheetApproval>>(
        URLs.TimesheetApprovals.byID(orgID, timesheetApprovalID),
        payload,
      )
      .then((res) => res.data.data)
  },

  delete: async (orgID: string, timesheetApprovalID: string): Promise<TimesheetApproval> => {
    return await api
      .delete<ApiResponse<TimesheetApproval>>(
        URLs.TimesheetApprovals.byID(orgID, timesheetApprovalID),
      )
      .then((res) => res.data.data)
  },
}

export const timesheetApprovalsApi: typeof timesheetApprovalsApiReal = __IS_MOCK_MODE__
  ? timesheetApprovalsApiMock
  : timesheetApprovalsApiReal
